<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { WhereUsedPanelDataQuery, WhereUsedPanelDataQueryVariables } from '@/generated/graphql'
import InlineBreadcrumb from '@/components/InlineBreadcrumb.vue'

const props = defineProps<{
  stepId: string
  modelValue: boolean
}>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const { t } = useI18n()

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const fetchQuery = useQuery<WhereUsedPanelDataQuery, WhereUsedPanelDataQueryVariables>(
  gql`
    query WhereUsedPanelData($id: ID!) {
      product {
        step(id: $id) {
          id
          usages {
            bop {
              id
              version
              productConfiguration {
                id
                name
                product {
                  id
                  name
                }
              }
              site {
                id
                name
              }
            }
            module {
              id
              name
            }
            step {
              id
              name
            }
          }
        }
      }
    }
  `,
  () => ({ id: props.stepId }),
  () => ({ enabled: isOpen.value }),
)
const usages = computed(() => fetchQuery.result.value?.product.step.usages ?? [])
const usedBopCount = computed(() => new Set(usages.value.map((u) => u.bop.id)).size)
</script>

<template>
  <v-dialog
    v-model="isOpen"
    transition="where-used-panel-transition"
    content-class="where-used-panel position-fixed h-100 rounded-0"
    width="440"
    scrollable
  >
    <v-card class="d-flex flex-column fill-height rounded-0">
      <v-toolbar density="compact" :title="t('view.organization.library.whereUsedTitle')">
        <v-btn icon="close" @click="isOpen = false" />
      </v-toolbar>

      <v-card-text>
        <v-progress-linear v-if="fetchQuery.loading.value" indeterminate class="mb-3" />

        <template v-else-if="usages.length">
          <p class="text-medium-emphasis mb-3">
            {{ t('view.organization.library.usedInBops', { count: usedBopCount }) }}
          </p>
          <template v-for="(usage, index) in usages" :key="`${usage.bop.id}-${usage.module.id}`">
            <v-divider v-if="index > 0" class="my-2" />
            <div class="py-1">
              <inline-breadcrumb
                :breadcrumb="{ bop: usage.bop, module: usage.module, step: usage.step }"
              />
            </div>
          </template>
        </template>

        <p v-else class="text-medium-emphasis">
          {{ t('view.organization.library.notInUse') }}
        </p>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style lang="scss">
.where-used-panel {
  top: 0;
  right: 0;
  margin: 0 !important;
  max-height: 100% !important;
}

.where-used-panel-transition-enter-active,
.where-used-panel-transition-leave-active {
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.where-used-panel-transition-enter-from,
.where-used-panel-transition-leave-to {
  transform: translateX(100%);
}
</style>
